import type { MouseEvent } from 'react';
import { useCallback } from 'react';

import type { ApiSlideshowData } from '#/api/types/ApiSlideshowData';
import { Carousel } from '#/components/Carousel/Carousel';
import { useCarouselState } from '#/components/Carousel/useCarouselState';
import { FilestackImage } from '#/components/FilestackImage/FilestackImage';
import { VideoThumbnail } from '#/components/VideoThumbnail/VideoThumbnail';
import { useIsClient } from '#/hooks/useIsClient';
import { useI18n } from '#/i18n/hooks/useI18n';

import { SlideshowItem } from './Slideshow.styled';
import { SlideshowAccessibleButton } from './SlideshowAccessibleButton';
import { SlideshowNavigation } from './SlideshowNavigation';

type Props = {
  carouselId: string;
  items: ApiSlideshowData[];
  size: number;
  onClickIndex?: (data: { itemIndex: number }) => void;
  qaId?: string;
};

export function Slideshow({
  carouselId,
  items,
  size,
  onClickIndex,
  qaId = 'carousel',
}: Props) {
  const { i18n } = useI18n();

  const carouselState = useCarouselState({
    slideCount: items.length,
    responsiveSlidesPerPage: {
      xs: 1,
      sm: 3,
      md: 3,
      lg: 3,
    },
    capSlidesPerPageToSlideCount: true,
  });

  const clickAccessibleButton = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      onClickIndex?.({ itemIndex: 0 });
    },
    [onClickIndex],
  );

  const isClient = useIsClient();

  return (
    <div style={{ position: 'relative' }}>
      {onClickIndex && (
        <SlideshowAccessibleButton
          aria-label={i18n('Open gallery')}
          title="Open image gallery"
          $navShown={carouselState.pageCount > 1}
          onClick={clickAccessibleButton}
        />
      )}

      <Carousel
        id={carouselId}
        state={carouselState}
        qaId={qaId}
        height={size}
        navigation={
          isClient && (
            <SlideshowNavigation
              carouselState={carouselState}
              fullWidth={false}
            />
          )
        }
        slides={items?.map((item, index) => (
          <SlideshowItem
            key={item.handle || item.videoUrl}
            $clickable={Boolean(onClickIndex)}
            data-qa-id={`${qaId}-image-container`}
            aria-label={i18n('Open gallery')}
            tabIndex={-1}
            onClick={(e: MouseEvent) => {
              e.preventDefault();
              onClickIndex?.({ itemIndex: index });
            }}
          >
            {item.fileId && (
              <FilestackImage
                qaId={`${qaId}-image`}
                data={item}
                key={item.handle}
                alt={item.altText ?? ''}
                width={size}
                height={size}
                objectFit="cover"
                style={{ borderRadius: '28px' }}
              />
            )}

            {item.type === 'VIDEO' && item.videoUrl && (
              <VideoThumbnail
                src={{ type: 'video-thumbnail', videoUrl: item.videoUrl }}
                qaId={`${qaId}-image`}
                size={{ type: 'expand' }}
                playIcon={{ size: 'large' }}
              />
            )}
          </SlideshowItem>
        ))}
      />
    </div>
  );
}
